// src/components/lesson/PracticalChallengeTemplate.jsx

import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import LessonShell from "./LessonShell";
import { supabase } from "../../supabaseClient";
import {
  buildActionCompletionPayload,
  readActionCompletion,
  saveActionCompletion,
} from "../../lib/learning/levelScoring";

const ACTION_PENGUIN_IMAGE =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Public%20Pages/Landing/gwin-penguin.png";

const TEMP_COMPLETE_IMAGE =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Stages/Gwin_Dance.png";

const DEFAULT_ACTION_OPTIONS = [
  {
    id: "cash",
    title: "Cash",
    description:
      "I am not sure what money is available or whether money timing is under control.",
    aScoreEligible: true,
  },
  {
    id: "records",
    title: "Records",
    description:
      "I am not sure whether my business money activity is clearly recorded.",
    aScoreEligible: true,
  },
  {
    id: "pricing",
    title: "Pricing",
    description: "I am not sure whether my prices make sense.",
    aScoreEligible: true,
  },
  {
    id: "compliance",
    title: "Compliance",
    description:
      "I am not sure whether an important business obligation needs attention.",
    aScoreEligible: true,
  },
];

function getOptionId(option, index) {
  return (
    option?.id ||
    option?._key ||
    option?.value ||
    option?.backendValue ||
    `option-${index + 1}`
  );
}

function getOptionTitle(option) {
  return (
    option?.title ||
    option?.label ||
    option?.text ||
    option?.optionText ||
    ""
  );
}

function getOptionDescription(option) {
  return (
    option?.description ||
    option?.supportText ||
    option?.helperText ||
    ""
  );
}

function normalizeOptions(options = []) {
  const sourceOptions =
    Array.isArray(options) && options.length > 0
      ? options
      : DEFAULT_ACTION_OPTIONS;

  return sourceOptions.map((option, index) => ({
    ...option,
    id: getOptionId(option, index),
    title: getOptionTitle(option),
    description: getOptionDescription(option),
  }));
}

function ActionPenguin({ src = ACTION_PENGUIN_IMAGE }) {
  if (!src) {
    return null;
  }

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className={[
        "pointer-events-none",
        "absolute",
        "z-0",
        "h-auto",
        "object-contain",
        "select-none",
      ].join(" ")}
      style={{
        width: "clamp(128px, 39vw, 240px)",
        right: "clamp(-100px, -20vw, -50px)",
        bottom: "clamp(-106px, -15.2vw, -55px)",
        transform: "rotate(-57deg)",
        opacity: 0.32,
      }}
      loading="eager"
      decoding="async"
      draggable="false"
    />
  );
}

export default function PracticalChallengesTemplate({
  title = "Action title",
  content = null,

  variant = "",

  levelKey = "",
  taskKey = "",
  taskMode = "application_task",
  // Pass the original Sanity actionQuestionDraftStep._key to enable trusted A-XP.
  questionKey = "",
  // Set true for Sanity-authored questions so a missing key never silently
  // falls back to browser-only completion. Legacy tasks keep their old path.
  requireVerifiedAction = false,
  onActionSubmit,

  onNext,
  backTo,
  backLabel = "Back",
  nextLabel = "NEXT",

  progressStep = 10,
  progressTotal = 11,

  actionLabel = "Your practical action",
  introText = "Short explanation",

  question = "Which money area must you check first in your business?",
  questionSupport = "Choose the option that is most relevant to your business.",
  options = DEFAULT_ACTION_OPTIONS,

  completionHeading = "Practical Action Completed!",
  completionText = "You now have one clear place to start.",

  actionImageSrc = ACTION_PENGUIN_IMAGE,
  completeImageSrc = TEMP_COMPLETE_IMAGE,

  className = "",
}) {
  const navigate = useNavigate();

  const controlledVariant = ["action", "question", "complete"].includes(
    String(variant || "").toLowerCase()
  );

  const initialScreen = controlledVariant
    ? String(variant).toLowerCase()
    : "intro";

  const [screen, setScreen] = useState(initialScreen);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const submittingRef = useRef(false);
  const [savedAction, setSavedAction] = useState(() =>
    readActionCompletion({ levelKey, taskKey })
  );

  const normalizedOptions = useMemo(
    () => normalizeOptions(options),
    [options]
  );

  const selectedOption =
    normalizedOptions.find(
      (option) => String(option.id) === String(selectedId)
    ) || null;

  const savedOptionTitle =
    savedAction?.selectedOptionTitle || selectedOption?.title || "";

  const goForward = () => {
    if (onNext) {
      onNext();
    }
  };

  const completeActionTask = async () => {
    if (!selectedOption) {
      throw new Error("Please choose an option before continuing.");
    }

    const payload = buildActionCompletionPayload({
      levelKey,
      taskKey,
      taskMode,
      selectedOption,
      answers: {
        selectedOptionId: selectedOption.id,
        selectedOptionTitle: selectedOption.title || "",
      },
    });

    const stableQuestionKey = String(questionKey || "").trim();
    if (requireVerifiedAction && !stableQuestionKey) {
      throw new Error("This Action Question is missing its Sanity identifier. Please contact support.");
    }

    let verification = null;
    if (stableQuestionKey) {
      if (!/^L[1-9]\d*$/.test(String(levelKey))) {
        throw new Error("This Action Question has an invalid level identifier.");
      }

      const { data, error } = await supabase.rpc("submit_my_learning_action_answer", {
        p_level_key: String(levelKey),
        p_question_key: stableQuestionKey,
        p_answer: {
          selectedOptionId: String(selectedOption.id),
          selectedOptionTitle: String(selectedOption.title || ""),
        },
      });
      if (error) throw error;
      if (!data || data.level_key !== String(levelKey)) {
        throw new Error("Could not confirm your Action Question response. Please try again.");
      }
      verification = data;
    }

    // Save the existing browser completion only after server confirmation.
    const savedPayload = saveActionCompletion(payload);
    setSavedAction(savedPayload);

    if (onActionSubmit) {
      await onActionSubmit(savedPayload, verification);
    }
    return savedPayload;
  };

  const handleNext = async () => {
    if (submittingRef.current) return;

    if (screen === "intro") {
      if (controlledVariant) goForward();
      else setScreen("question");
      return;
    }
    if (screen === "action" || screen === "complete") {
      goForward();
      return;
    }
    if (screen !== "question" || !selectedOption) return;

    submittingRef.current = true;
    setSubmitting(true);
    setSubmissionError("");
    try {
      await completeActionTask();
      if (controlledVariant) goForward();
      else setScreen("complete");
    } catch (error) {
      console.error("[Gwin] Action Question submission failed:", error);
      setSubmissionError("We couldn't save your answer. Please try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (!controlledVariant && screen === "question") {
      setScreen("intro");
      return;
    }

    if (!controlledVariant && screen === "complete") {
      setScreen("question");
      return;
    }

    if (backTo) {
      navigate(backTo);
      return;
    }

    navigate(-1);
  };

  if (screen === "intro" || screen === "action") {
    return (
      <LessonShell
        progressStep={progressStep}
        progressTotal={progressTotal}
        showBack
        backLabel={backLabel}
        onBack={handleBack}
        showNext
        nextLabel={nextLabel || "NEXT"}
        onNext={handleNext}
        className={className}
        contentClassName="pt-10"
      >
        <section
          className={[
            "relative",
            "mx-auto",
            "flex",
            "w-full",
            "max-w-[590px]",
            "flex-col",
            "overflow-visible",
          ].join(" ")}
          style={{
            minHeight: "clamp(390px, 58dvh, 520px)",
            paddingBottom: "clamp(18px, 5vw, 38px)",
          }}
        >
          <div className="relative z-10">
            {!!actionLabel && (
              <p className="text-[18px] font-extrabold uppercase text-[#FF7A3D]">
                {actionLabel}
              </p>
            )}

            <h1 className="mt-8 max-w-[500px] text-[34px] font-extrabold leading-[1.12] tracking-[-0.03em] text-[#111111] sm:text-[42px]">
              {title}
            </h1>

            {!!introText && (
              <p className="mt-8 max-w-[500px] text-[18px] font-medium leading-[1.55] text-[#777777]">
                {introText}
              </p>
            )}

            {!!content && (
              <div className="mt-6 max-w-[500px] text-[15px] font-medium leading-[1.65] text-[#555555]">
                {content}
              </div>
            )}
          </div>

          <ActionPenguin src={actionImageSrc} />
        </section>
      </LessonShell>
    );
  }

  if (screen === "question") {
    return (
      <LessonShell
        progressStep={progressStep}
        progressTotal={progressTotal}
        showBack
        backLabel={backLabel}
        onBack={handleBack}
        showNext
        nextLabel={nextLabel || "NEXT"}
        onNext={handleNext}
        nextDisabled={!selectedOption || submitting}
        className={className}
        contentClassName="pt-8"
      >
        <section
          className={[
            "relative",
            "mx-auto",
            "w-full",
            "max-w-[590px]",
            "overflow-visible",
          ].join(" ")}
          style={{
            paddingBottom: "clamp(18px, 5vw, 38px)",
          }}
        >
          <div className="relative z-10">
            <h1 className="max-w-[520px] text-[30px] font-extrabold leading-[1.13] tracking-[-0.025em] text-[#111111] sm:text-[36px]">
              {question}
            </h1>

            {!!questionSupport && (
              <p className="mt-4 max-w-[500px] text-[16px] font-medium leading-[1.5] text-[#7A7A7A]">
                {questionSupport}
              </p>
            )}

            {!!submissionError && (
              <p role="alert" className="mt-4 text-sm font-semibold text-red-600">
                {submissionError}
              </p>
            )}

            {submitting && (
              <p role="status" className="mt-4 text-sm text-[#777777]">
                Saving your answer...
              </p>
            )}

            <div className="mt-8 space-y-4">
              {normalizedOptions.map((option, index) => {
                const selected =
                  String(option.id) === String(selectedId);

                return (
                  <button
                    key={option.id || index}
                    type="button"
                    onClick={() => {
                      if (submitting) return;
                      setSelectedId(option.id);
                      setSubmissionError("");
                    }}
                    disabled={submitting}
                    className={[
                      "flex",
                      "min-h-[92px]",
                      "w-full",
                      "items-center",
                      "rounded-[18px]",
                      "border-2",
                      "bg-white",
                      "px-5",
                      "py-4",
                      "text-left",
                      "transition-all",
                      selected
                        ? "border-[#FF914D] shadow-[0_4px_14px_rgba(255,145,77,0.10)]"
                        : "border-[#DEDEDE] hover:border-[#FFB27D]",
                    ].join(" ")}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[16px] font-extrabold text-[#111111]">
                        {option.title}
                      </p>

                      {!!option.description && (
                        <p className="mt-1 text-[13px] italic leading-[1.45] text-[#8A8A8A]">
                          “{option.description}”
                        </p>
                      )}
                    </div>

                    <span
                      className="ml-4 flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full border-2 border-[#D8D8D8]"
                      aria-hidden="true"
                    >
                      {selected && (
                        <span className="h-[24px] w-[24px] rounded-full bg-[#FF914D]" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <ActionPenguin src={actionImageSrc} />
        </section>
      </LessonShell>
    );
  }

  return (
    <LessonShell
      progressStep={progressStep}
      progressTotal={progressTotal}
      showBack={false}
      showNext
      nextLabel={nextLabel || "NEXT"}
      onNext={handleNext}
      className={className}
      contentClassName="pt-8"
    >
      <section className="mx-auto flex w-full max-w-[560px] flex-col items-center text-center">
        {!!completeImageSrc && (
          <img
            src={completeImageSrc}
            alt=""
            aria-hidden="true"
            className="mt-16 h-auto w-[225px] max-w-[48vw] object-contain"
            loading="eager"
            decoding="async"
            draggable="false"
          />
        )}

        <h1 className="mt-7 text-[35px] font-extrabold leading-[1.08] tracking-[-0.035em] text-[#111111] sm:text-[42px]">
          {completionHeading}
        </h1>

        {!!savedOptionTitle && (
          <p className="mt-10 text-[16px] font-medium leading-[1.55] text-[#4A4A4A]">
            Your first money focus:{" "}
            <span className="font-extrabold text-[#111111]">
              {savedOptionTitle}
            </span>
          </p>
        )}

        {!!completionText && (
          <p className="mt-7 text-[16px] font-medium leading-[1.55] text-[#4A4A4A]">
            {completionText}
          </p>
        )}
      </section>
    </LessonShell>
  );
}